/*
 * Jira Ticket:
 * Created Date: Tue, 17th Mar 2020, 14:12:23 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { useState, useEffect } from 'react';
import {
  BooleanInput,
  Create,
  required,
  SimpleForm,
  TextInput,
  usePermissions,
  SelectInput,
  useDataProvider,
  fetchStart,
  fetchEnd,
  Title,
  useNotify,
  FormDataConsumer,
} from 'react-admin';
import { useDispatch } from 'react-redux';
import { AccessDenied } from 'Layout';
import { isManager } from 'utils/roles';
import { checklistTypes } from 'utils/choices';
import { areChecklistTypesExtended } from 'utils/capabilities';
import * as R from 'ramda';
import { usePersistentOrganisation } from '../../Components/Contexts';
import getOrganisationInfo from '../../utils/getOrganisationInfo';

const ProjectCreate = props => {
  const notify = useNotify();
  const dispatch = useDispatch();
  const dataProvider = useDataProvider();
  const { permissions } = usePermissions();
  const [
    propOrgHasExtendedChecklistCapability,
    setPropOrgHasExtendedChecklistCapability,
  ] = useState(false);
  const [orgIdProp, setOrgIdProp] = useState();
  const [organisations, setOrganisations] = useState([]);
  const [organisationChoices, setOrganisationChoices] = useState([]);
  const { persistentOrganisation } = usePersistentOrganisation();
  // MARK: - Setup

  // Get org selection options
  const getOrganisationOptions = () => {
    dispatch(fetchStart());
    dataProvider('GET_LIST', 'Organisation', {
      pagination: { page: 1, perPage: 1000 },
    })
      .then(result => {
        const orgs = R.path(['data'], result) || [];
        setOrganisations(orgs);
      })
      .catch()
      .finally(() => {
        dispatch(fetchEnd());
      });
    return () => {};
  };

  // Check wether org id has been passed by navigation
  const setUp = () => {
    if (persistentOrganisation.id) {
      setOrgIdProp({ organisationId: persistentOrganisation.id });
      getOrganisationOptions();
    } else {
      getOrganisationOptions();
    }
    return () => {};
  };

  const useMountEffect = fun => useEffect(fun, []);
  useMountEffect(setUp); // run once
  useMountEffect(
    getOrganisationInfo(persistentOrganisation.id, {
      dataProvider,
      dispatch,
      notify,
      setOrganisationCapabilities: capabilities =>
        setPropOrgHasExtendedChecklistCapability(
          areChecklistTypesExtended(capabilities)
        ),
    })
  );

  // MARK: - Local functions

  // Organisations select options based on permissions
  useEffect(() => {
    const choices = organisations
      .filter(org => isManager(permissions, org.id))
      .map(permittedOrg => {
        return { id: permittedOrg.id, name: permittedOrg.name };
      });
    setOrganisationChoices(choices);
  }, [organisations, permissions]);

  const orgHasExtendedChecklistTypesCapability = orgId => {
    const org = organisations.find(org => org.id === orgId);
    const capabilities = R.path(
      ['subscription', 'subscriptionTier', 'capabilities'],
      org
    );
    const hasCapability = areChecklistTypesExtended(capabilities);
    return hasCapability;
  };

  // MARK: Rendering

  // if (orgIdProp && !isManager(permissions, orgIdProp)) {
  //   return <AccessDenied />;
  // }

  return (
    <>
      <Title title="resources.General.cmsTitle" />
      <Create {...props}>
        <SimpleForm
          redirect={
            orgIdProp
              ? `/Organisation/${orgIdProp.organisationId}/1`
              : undefined
          }
        >
          <TextInput
            source="name"
            validate={required()}
            label="resources.Project.fields.project"
          />
          {!orgIdProp ? (
            <SelectInput
              source="organisationId"
              label="resources.Project.fields.organisation"
              choices={organisationChoices}
            >
              <SelectInput source="id" optionText="name" />
            </SelectInput>
          ) : (
            <SelectInput
              source="organisationId"
              label="resources.Project.fields.organisation"
              choices={organisationChoices}
              initialValue={orgIdProp.organisationId}
            >
              <SelectInput source="id" optionText="name" />
            </SelectInput>
          )}
          <BooleanInput
            source="siteCreationEnabled"
            defaultValue={false}
            label="resources.Project.fields.allowSites"
          />
           <BooleanInput
            source="showTowerImages"
            defaultValue={false}
            label="resources.Project.fields.allowTowerImages"
          />
          <FormDataConsumer>
            {({ formData, ...rest }) => {
              const { organisationId } = formData;
              const hasExtendedChecklistTypesCapability = orgHasExtendedChecklistTypesCapability(
                organisationId
              );
              const hasCapability =
                propOrgHasExtendedChecklistCapability ||
                hasExtendedChecklistTypesCapability;
              return (
                <SelectInput
                  source="checklistType"
                  label="resources.Project.fields.checklistType"
                  choices={checklistTypes}
                  validate={required()}
                  disabled={!hasCapability}
                  defaultValue="IN_APP_AUTO_APPROVE"
                />
              );
            }}
          </FormDataConsumer>
        </SimpleForm>
      </Create>
    </>
  );
};

export default ProjectCreate;
